import React from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'

export const SignatureDialog = ({ open, value = '', onClose, onSave, title = 'Firma del responsable', confirmLabel = 'Guardar firma', signerName, busy = false, error, autoClose = true }) => {
  const titleId = React.useId()
  const [hasInk, setHasInk] = React.useState(false)
  const [ready, setReady] = React.useState(false)
  const canvasRef = React.useRef(null)
  const initializedRef = React.useRef(false)
  const drawingRef = React.useRef(false)
  const lastPointRef = React.useRef(null)

  const prepareCanvas = React.useCallback((preserveDrawing = false) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return
    const ratio = window.devicePixelRatio || 1
    const width = Math.max(1, Math.floor(rect.width * ratio))
    const height = Math.max(1, Math.floor(rect.height * ratio))
    const preserve = preserveDrawing && initializedRef.current
    if (preserve && canvas.width === width && canvas.height === height) return
    const previous = preserve ? document.createElement('canvas') : null
    if (previous) {
      previous.width = canvas.width
      previous.height = canvas.height
      previous.getContext('2d').drawImage(canvas, 0, 0)
    }
    canvas.width = width
    canvas.height = height
    drawingRef.current = false
    lastPointRef.current = null

    const context = canvas.getContext('2d')
    context.setTransform(ratio, 0, 0, ratio, 0, 0)
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, rect.width, rect.height)
    context.strokeStyle = '#111111'
    context.lineWidth = 2.4
    context.lineCap = 'round'
    context.lineJoin = 'round'
    initializedRef.current = true
    setReady(rect.width > 0 && rect.height > 0)

    if (previous) {
      context.drawImage(previous, 0, 0, rect.width, rect.height)
    } else if (value) {
      const image = new Image()
      image.onload = () => {
        context.drawImage(image, 0, 0, rect.width, rect.height)
      }
      image.src = value
    }
  }, [value])

  React.useEffect(() => {
    if (!open) return undefined
    setHasInk(Boolean(value))
    setReady(false)
    initializedRef.current = false
    let observer
    const resize = () => prepareCanvas(true)
    window.addEventListener('resize', resize)
    const timeout = window.setTimeout(() => {
      prepareCanvas()
      if (typeof ResizeObserver !== 'undefined' && canvasRef.current) {
        observer = new ResizeObserver(() => prepareCanvas(true))
        observer.observe(canvasRef.current)
      }
    }, 0)
    return () => { window.clearTimeout(timeout); observer?.disconnect(); window.removeEventListener('resize', resize) }
  }, [open, prepareCanvas, value])

  const getPoint = (event) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }
  }

  const handlePointerDown = (event) => {
    if (busy || !ready) return
    event.preventDefault()
    const canvas = canvasRef.current
    // Some embedded mobile browsers cannot capture the active pointer. Drawing
    // inside the canvas must still work when capture is absent or rejected.
    try { canvas.setPointerCapture?.(event.pointerId) } catch { /* Continue drawing without capture. */ }
    drawingRef.current = true
    lastPointRef.current = getPoint(event)
    const context = canvas.getContext('2d')
    context.beginPath()
    context.fillStyle = '#111111'
    context.arc(lastPointRef.current.x, lastPointRef.current.y, 1.2, 0, Math.PI * 2)
    context.fill()
    setHasInk(true)
  }

  const handlePointerMove = (event) => {
    if (!drawingRef.current || busy) return
    event.preventDefault()

    const canvas = canvasRef.current
    const context = canvas.getContext('2d')
    const point = getPoint(event)
    const lastPoint = lastPointRef.current || point

    context.beginPath()
    context.moveTo(lastPoint.x, lastPoint.y)
    context.lineTo(point.x, point.y)
    context.stroke()

    lastPointRef.current = point
  }

  const handlePointerUp = (event) => {
    if (canvasRef.current?.hasPointerCapture?.(event.pointerId)) {
      canvasRef.current.releasePointerCapture(event.pointerId)
    }
    drawingRef.current = false
    lastPointRef.current = null
  }

  const handleClear = () => {
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')
    const rect = canvas.getBoundingClientRect()
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, rect.width, rect.height)
    setHasInk(false)
  }

  const handleSave = () => {
    if (!hasInk || !ready || busy) return
    onSave(canvasRef.current.toDataURL('image/png'))
    if (autoClose) onClose()
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth className="quality-mobile-dialog quality-signature-dialog" aria-labelledby={titleId} slotProps={{ transition: { onEntered: () => prepareCanvas(true) } }}>
      <DialogTitle id={`${titleId}-container`} className="quality-signature-dialog-title">
        <Box>
          <Typography variant="h6" id={titleId}>{title}</Typography>
          <Typography>Firma en el recuadro con el dedo, lapiz o raton.</Typography>
          {signerName ? <Typography>Firmante: {signerName}</Typography> : null}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <canvas
          ref={canvasRef}
          className="quality-signature-canvas"
          aria-label="Area para firmar"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClear} disabled={busy || !ready}>Limpiar</Button>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={!hasInk || !ready || busy}>{busy ? 'Guardando...' : confirmLabel}</Button>
      </DialogActions>
    </Dialog>
  )
}

